import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { LinearProgress, Box, Button } from '@mui/material'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { H3 } from '../../../layouts/TextStyles'
import { courseUserApiSlice } from '../../../features/course/courseUserSlice'
import { userApiSlice } from '../../../features/user/userSlice'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import AddStudentDialog from './components/AddStudentDialog'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'
import { ui } from '../../../utils/uiTokens'

const StudentListPage = () => {
    const { formatMessage } = useIntl()
    const [usersInfo, setUsersInfo] = useState([])
    const [filter, setFilter] = useState('')
    // Поиск применяется кнопкой или по Enter, а не на каждое нажатие клавиши
    const [search, setSearch] = useState('')
    const [fetchStudents] = studentApiSlice.useLazyFetchAllStudentsQuery()

    const [deleteUser, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [getCourseUsersInfoMutation, { isLoading: isLoadingUsersInfo }] =
        courseUserApiSlice.useGetCourseUsersInfoMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()
    const [sendWelcomeMessage, { isLoading: isSendingWelcomeMessage }] =
        userApiSlice.useSendWelcomeMessageMutation()

    const { rows, isLoading, loadedPages, sentinelRef, reload } =
        usePaginatedList(
            (page, size) =>
                fetchStudents({ page, size, filter: search }).unwrap(),
            [search],
        )

    const handleDelete = async (id: number) => {
        try {
            await deleteUser(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (error) { }
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            reload()
        } catch (error) { }
    }

    const getCourseUsersInfo = async () => {
        try {
            const result = await getCourseUsersInfoMutation(
                rows.map((item) => item.id),
            ).unwrap()
            setUsersInfo(result)
        } catch (error) { }
    }

    const handleSendWelcomeMessage = async (id: number) => {
        try {
            await sendWelcomeMessage(id).unwrap()
            toast.success(formatMessage({ id: 'sentSuccess' }))
            reload()
        } catch (error) { }
    }

    useEffect(() => {
        if (rows.length) {
            getCourseUsersInfo()
        }
    }, [rows])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>

                    <AddStudentDialog refetchStudents={() => reload()} />
                </Header>

                <SearchBox>
                    <CustomInput
                        sx={{ width: "400px" }}
                        margin="none"
                        type="text"
                        placeholder={formatMessage({ id: 'search' })}
                        size="small"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setSearch(filter)
                        }}
                    />

                    <Button
                        variant="contained"
                        onClick={() => setSearch(filter)}
                    >
                        {formatMessage({ id: 'search' })}
                    </Button>
                </SearchBox>

                {(isLoading && loadedPages === 0) ||
                isReseting ||
                isDeleting ||
                isSendingWelcomeMessage ? (
                    <LinearProgress />
                ) : (
                    <>
                        <StudentsTable
                            studentsData={rows}
                            usersInfo={usersInfo}
                            isFetching={isLoading}
                            isLoadingUsersInfo={isLoadingUsersInfo}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                            onSendWelcomeMessage={handleSendWelcomeMessage}
                        />

                        <Sentinel ref={sentinelRef} isLoading={isLoading} />
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default StudentListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const SearchBox = styled(Box)`
    display: flex; 
    align-items: center;
    gap: 8px;
    margin: 8px 0;
`
